import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";

import { ROUTES } from "../_constant/screens";

import SaveConnection from "../containers/SaveConnection";
import MainWrapper from "../components/MainWrapper/MainWrapper";
import { GlobalLoader } from "../components/Loader";

interface ProtectedRouteProps {
  component: React.ComponentType;
  withoutWrapper?: boolean;
}

/** Route element that requires a signed-in user with a verified email address. */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  component: Component,
  withoutWrapper = false,
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { isAuthenticated, user } = useAuth0();

  useEffect(() => {
    if (isAuthenticated && user?.email_verified === false) {
      navigate(ROUTES.VERIFY_EMAIL);
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (search.includes("?error=unauthorized")) {
      let msg = search.split("error_description=")[1]?.split("&")[0];
      while (typeof msg === "string" && msg.length > 0 && !msg.includes(" ")) {
        msg = decodeURI(msg);
      }
      navigate(ROUTES.ERROR, { state: { error: msg } });
    }
  }, [search, navigate]);

  const Child = useMemo(() => {
    const ComponentWithWrapper: React.FC = withoutWrapper
      ? Component
      : () => (
          <MainWrapper>
            {/* Pages load lazily; keep the header and menu up while a page's code arrives. */}
            <React.Suspense fallback={<GlobalLoader />}>
              <Component />
            </React.Suspense>
          </MainWrapper>
        );
    return withAuthenticationRequired(ComponentWithWrapper, {
      onRedirecting: () => <SaveConnection />,
    });
  }, [Component, withoutWrapper]);

  return <Child />;
};

export default ProtectedRoute;
